<template>
  <div class="user-search" ref="searchContainer">
    <div class="input-wrapper">
      <input type="text" v-model="searchQuery" placeholder="Search in messages..." @input="debouncedSearch"
        class="search-input" @focus="handleFocus" />
      <button v-if="searchQuery" class="clear-button" @click="clearSearch" aria-label="Clear">
        ✖
      </button>
    </div>

    <div v-if="isSearching" class="lds-grid" style="justify-items: center">
      <div class="lds-ripple">
        <div></div>
        <div></div>
      </div>
      <p>Searching notifications...</p>
    </div>

    <div v-if="showDropdown && searchResults.length" class="search-results">
      <a v-for="(message, index) in uniqueSenders" :key="message.sender.id"
        :href="'/dashboard/patients/' + message.sender.id + '?mark=true'"
        class="search-result-item flex justify-between items-center p-2 hover:bg-gray-100">
        <div>
          <div class="text-left font-semibold">
            <span
              v-for="(part, idx) in highlightSearchTermParts(message.sender.firstName + ' ' + message.sender.lastName)"
              :key="'label-' + idx" :class="{ highlight: part.highlight }">{{ part.text }}</span>
          </div>
          <div style="font-size: 12px">
            <span v-for="(part, idx) in highlightSearchTermParts(message.body)" :key="'body-' + idx"
              :class="{ highlight: part.highlight }">
              {{ part.text }}
            </span>
          </div>
        </div>
      </a>
    </div>

    <div v-if="searchQuery && !searchResults.length && !isSearching && searchPerformed && showDropdown"
      class="no-results">
      No users found.
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';

export default {
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      isSearching: false,
      searchPerformed: false,
      debouncedSearch: null,
      showDropdown: false,
    };
  },
  created() {
    this.debouncedSearch = debounce(this.searchUsers, 300);
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeyDown);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  created() {
    this.debouncedSearch = debounce(this.searchUsers, 300);
  },
  computed: {
    uniqueSenders() {
      const seen = new Set();
      return this.searchResults.filter((msg) => {
        if (seen.has(msg.sender.id)) return false;
        seen.add(msg.sender.id);
        return true;
      });
    },
  },
  methods: {
    searchUsers() {
      const query = this.searchQuery.trim();

      if (!query) {
        this.searchResults = [];
        this.searchPerformed = false;
        this.showDropdown = false;
        return;
      }

      this.isSearching = true;
      this.searchPerformed = true;

      axios
        .get(`/api/v1/messages/user?search=${encodeURIComponent(query)}`)
        .then((response) => {
          this.searchResults = response.data.data || [];
          this.showDropdown = true;
        })
        .catch((error) => {
          console.error('Error searching users:', error);
          this.searchResults = [];
          this.showDropdown = true;
        })
        .finally(() => {
          this.isSearching = false;
        });
    },

    highlightSearchTermParts(text) {
      if (!this.searchQuery || !text) return [{ text, highlight: false }];

      const query = this.searchQuery.trim();
      const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');

      const parts = [];
      let lastIndex = 0;

      text.replace(regex, (match, p1, offset) => {
        if (offset > lastIndex) {
          parts.push({ text: text.substring(lastIndex, offset), highlight: false });
        }
        parts.push({ text: match, highlight: true });
        lastIndex = offset + match.length;
      });

      if (lastIndex < text.length) {
        parts.push({ text: text.substring(lastIndex), highlight: false });
      }

      return parts;
    },

    escapeRegex(text) {
      return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    },

    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      this.searchPerformed = false;
      this.showDropdown = false;
    },

    handleFocus() {
      if (this.searchQuery && this.searchResults.length) {
        this.showDropdown = true;
      }
    },

    handleOutsideClick(event) {
      const container = this.$refs.searchContainer;
      if (container && !container.contains(event.target)) {
        this.showDropdown = false;
      }
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.showDropdown = false;
      }
    },
  }
};
</script>

<style scoped>
.highlight {
  background-color: yellow;
  color: black;
}

.user-search {
  position: relative;
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  outline: none;
}

.search-input:focus {
  border-color: #243153;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
}

.search-result-item {
  display: block;
  padding: 10px;
  color: #243153 !important;
  text-decoration: none !important;
}

.search-result-item:hover {
  background-color: #f0f8ff;
  text-decoration: underline;
}

.no-results {
  padding: 10px;
  color: #949494;
  text-align: center;
}

.input-wrapper {
  position: relative;
}

.clear-button {
  position: absolute;
  right: 10px;
  top: 56%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  font-size: 1.2rem;
  color: #3c425a;
  cursor: pointer;
  padding: 0;
}

.clear-button:hover {
  color: #004572;
}

.search-input {
  width: 100%;
  padding: 10px 2rem 10px 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  outline: none;
}

.lds-ripple,
.lds-ripple div {
  box-sizing: border-box;
}

.lds-ripple {
  display: flex;
  position: relative;
  width: 80px;
  height: 80px;
}

.lds-ripple div {
  position: absolute;
  border: 4px solid #243153;
  opacity: 1;
  border-radius: 50%;
  animation: lds-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}

.lds-ripple div:nth-child(2) {
  animation-delay: -0.5s;
}

@keyframes lds-ripple {
  0% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 0;
  }

  4.9% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 0;
  }

  5% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 1;
  }

  100% {
    top: 0;
    left: 0;
    width: 80px;
    height: 80px;
    opacity: 0;
  }
}

.lds-grid p {
  color: #243153;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/SearchUsers.vue